import { DateTime } from 'luxon';
import { ActivityTimeline, RenderIf, Spinner } from 'react-rainbow-components';
import ActivityAvatar from 'components/ActivityAvatar';
import Button from 'components/Button';
import { Name, SecondaryText } from 'components/CustomerActivity/styled';
import Check from 'components/icons/check';
import LabelIcon from 'components/icons/label';
import getInitials from 'data/services/string/getInitials';
import { InvoiceBuilderApiError, useInvoiceActivities } from '../api/hooks';
import {
    activityActorName,
    activityEditNote,
    activityFallbackLine,
    activityHeaderLabel,
    describedActivityChanges,
} from '../invoiceActivity';
import { InvoiceActivity } from '../types';
import { EmptyDescription, EmptyState } from '../styled';
import {
    HistoryBubble,
    HistoryChangeCard,
    HistoryChangeLabel,
    HistoryChangeMark,
    HistoryChangeRow,
    HistoryChangeRows,
    HistoryChangeValues,
    HistoryEditNote,
    HistoryLoadMore,
    HistoryMarker,
    HistoryPane,
    HistoryReason,
    HistoryTime,
    HistoryTitle,
    HistoryValueNext,
    HistoryValuePill,
} from './styled';

const formatActivityTime = (value: string) => {
    const date = DateTime.fromISO(value);
    if (!date.isValid) return value;
    const time = date.toFormat('t');
    const today = DateTime.now();
    if (date.hasSame(today, 'day')) return `Today, ${time}`;
    if (date.hasSame(today.minus({ day: 1 }), 'day')) return `Yesterday, ${time}`;
    const day = date.hasSame(today, 'year')
        ? date.toFormat('LLL d')
        : date.toFormat('LLL d, yyyy');
    return `${day}, ${time}`;
};

const ChangeLine = ({
    label,
    previous,
    next,
}: {
    label: string;
    previous: string;
    next: string;
}) => (
    <HistoryChangeRow>
        <HistoryChangeLabel>{label}</HistoryChangeLabel>
        <HistoryChangeValues>
            <RenderIf isTrue={Boolean(previous)}>
                <HistoryValuePill>{previous}</HistoryValuePill>
            </RenderIf>
            <RenderIf isTrue={Boolean(previous && next)}>
                <span>→</span>
            </RenderIf>
            <RenderIf isTrue={Boolean(next)}>
                <HistoryValueNext>{next}</HistoryValueNext>
            </RenderIf>
        </HistoryChangeValues>
    </HistoryChangeRow>
);

interface ActivityRowProps {
    activity: InvoiceActivity;
    currency: string;
    locale: string;
}

const ActivityRow = ({ activity, currency, locale }: ActivityRowProps) => {
    const name = activityActorName(activity.actor);
    const changes = describedActivityChanges(activity.changes || [], currency, locale);
    const reason = activity.editAudit?.changeReason?.trim() || '';
    const editNote = activityEditNote(activity.editAudit).replace(
        'invoice was void.',
        'invoice was voided.',
    );
    const lines = changes.map((change) => (
        <ChangeLine
            key={change.key}
            label={change.label}
            previous={change.previous}
            next={change.next}
        />
    ));
    const fallback = lines.length ? '' : activityFallbackLine(activity.kind);
    const showDetails = Boolean(editNote || reason || lines.length);
    return (
        <HistoryMarker
            icon={(
                <ActivityAvatar
                    src={activity.actor.photoURL || undefined}
                    initials={name === 'Unknown' ? undefined : getInitials(name)}
                    value={activity.actor.uid}
                />
            )}
            label={(
                <HistoryTitle>
                    <Name>{name}</Name>
                    <SecondaryText>{activityHeaderLabel(activity)}</SecondaryText>
                </HistoryTitle>
            )}
            datetime={<HistoryTime>{formatActivityTime(activity.occurredAt)}</HistoryTime>}
        >
            <RenderIf isTrue={Boolean(fallback) && !showDetails}>
                <HistoryBubble isCurrentUser={false} isFirst isLast>
                    {fallback}
                </HistoryBubble>
            </RenderIf>
            <RenderIf isTrue={showDetails}>
                <HistoryBubble isCurrentUser={false} isFirst isLast>
                    <RenderIf isTrue={Boolean(editNote)}>
                        <HistoryEditNote>{editNote}</HistoryEditNote>
                    </RenderIf>
                    <RenderIf isTrue={lines.length > 0}>
                        <HistoryChangeCard>
                            <HistoryChangeMark>
                                <span>
                                    <Check />
                                </span>
                            </HistoryChangeMark>
                            <HistoryChangeRows>{lines}</HistoryChangeRows>
                        </HistoryChangeCard>
                    </RenderIf>
                    <RenderIf isTrue={Boolean(reason)}>
                        <HistoryReason>
                            <LabelIcon />
                            <span>Reason:</span>
                            <strong>{reason}</strong>
                        </HistoryReason>
                    </RenderIf>
                </HistoryBubble>
            </RenderIf>
        </HistoryMarker>
    );
};

interface InvoiceHistoryProps {
    organizationId: string;
    invoiceId: string;
    currency?: string;
    locale?: string;
}

const InvoiceHistory = ({
    organizationId,
    invoiceId,
    currency = 'USD',
    locale = 'en-US',
}: InvoiceHistoryProps) => {
    const {
        data,
        error,
        isLoading,
        isError,
        hasNextPage,
        isFetchingNextPage,
        fetchNextPage,
    } = useInvoiceActivities(organizationId, invoiceId);
    const activities = data?.pages.flatMap((page) => page.data.activities) || [];
    const message = error instanceof InvoiceBuilderApiError
        ? error.message
        : 'Could not load history.';

    if (isLoading) {
        return (
            <EmptyState>
                <Spinner />
            </EmptyState>
        );
    }

    if (!activities.length && isError) {
        return (
            <EmptyState>
                <EmptyDescription>{message}</EmptyDescription>
            </EmptyState>
        );
    }

    if (!activities.length) {
        return (
            <EmptyState>
                <EmptyDescription>There is no activity</EmptyDescription>
            </EmptyState>
        );
    }

    return (
        <HistoryPane>
            <ActivityTimeline>
                {activities.map((activity) => (
                    <ActivityRow
                        key={activity.id}
                        activity={activity}
                        currency={currency}
                        locale={locale}
                    />
                ))}
            </ActivityTimeline>
            <RenderIf isTrue={isError}>
                <EmptyDescription>{message}</EmptyDescription>
            </RenderIf>
            <RenderIf isTrue={Boolean(hasNextPage)}>
                <HistoryLoadMore>
                    <Button
                        label={isFetchingNextPage ? 'Loading…' : 'Load older'}
                        variant="base"
                        size="small"
                        borderRadius="semi-rounded"
                        disabled={isFetchingNextPage}
                        onClick={() => fetchNextPage()}
                    />
                </HistoryLoadMore>
            </RenderIf>
        </HistoryPane>
    );
};

export default InvoiceHistory;
